import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import base from '../styles/base.css?inline';
import { flattenOutline, reportTableSpec } from '@mv/models';
import { api } from '../api.js';
import { formatDateTime } from '../utils/datetime.js';
import { buildBoorprofielData } from '../utils/boorprofiel-data.js';
import { boorprofielPaginas } from '../utils/boorprofiel-pdf.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { reportUiStyles, SOURCE_TAGS, BLOCK_KINDS, REPORT_STATUS, fieldTag } from '../utils/report-ui.js';

/** Achtergronden die het format voor kop- en groepsrijen gebruikt; in de editor doet de stijl dat. */
const FORMAT_FILLS = new Set(['BFBFBF', 'F2F2F2']);

/** Een leeg tekstblok: wat er komt, en wanneer. */
const PENDING = {
  generatedText: 'Flip AI schrijft hier later een voorstel op basis van de tabellen en de toetsing.',
  manualText: 'Door de adviseur te schrijven.',
  fixedText: 'Er is nog geen bureautekst voor deze paragraaf.',
};

/**
 * Status van een bijlage uit de feiten van het rapport. `null` = vast onderdeel, altijd klaar.
 */
const APPENDIX_READY = {
  'appendix.samplingPlan': (f) => (f['sublocations.count'] || 0) > 0,
  'appendix.boreholeLogs': (f) => (f['borings.count'] || 0) > 0,
  'appendix.labCertificates': (f) => (f['analysisSamples.withResults'] || 0) > 0,
  'appendix.assessments': (f) => !!f['toets.available'],
  'appendix.preliminaryInvestigation': (f) => !!f['fieldOrder.available'],
  'appendix.principles': () => true,
  'appendix.crow400': (f) => f['crow400.class'] != null,
};

/** Bron van een tabelkolom (docs/report-table-mapping.json), als label. */
const columnTag = (src) => {
  const s = String(src || '').toLowerCase();
  if (/lab|analys|result|toets/.test(s)) return 'lab';
  if (/sublocation|deellocatie/.test(s)) return 'sublocations';
  if (/boring|layer|laag|monster|peilbuis|pot/.test(s)) return 'borings';
  return 'report';
};

/**
 * report-editor — één rapport bekijken en bewerken (featureplan rapportage §12).
 *
 * Drie kolommen: links de inhoud (met een rood bolletje waar nog iets open staat en een paars
 * waar Flip AI nog moet schrijven), in het midden het document zelf, rechts 'Waar komt dit
 * vandaan' voor het gekozen blok. Elk blok zegt wat het is (uit de data, vaste tekst, voorstel
 * Flip AI) en kan worden vastgezet: opnieuw vullen laat het dan met rust.
 *
 * Live: de editor luistert op de WebSocket van het project. Verandert er iets aan de data,
 * dan haalt hij het rapport opnieuw op en markeert de backend de blokken waarvan de bron afwijkt.
 *
 * @fires close
 */
export class ReportEditor extends LitElement {
  static properties = {
    reportId: { type: String },
    projectId: { type: String },
    /** Ruwe boringen van het project (API-vorm), voor de boorprofielen in bijlage 2. */
    projectBorings: { type: Array },
    /** Net aangemaakt: meteen vullen uit de data. */
    fillOnOpen: { type: Boolean },
    _word: { state: true },         // { status, error, result } van het genereren
    _data: { state: true },
    _loading: { state: true },
    _filling: { state: true },
    _error: { state: true },
    _selected: { state: true },     // blok-id voor het paneel rechts
    _activeSec: { state: true },    // sectie die in de inhoud oplicht
    _savingBlock: { state: true },
    _variantOpen: { state: true },  // blok-id waarvan 'Andere variant' openstaat
    _variantPick: { state: true },
    _writing: { state: true },      // blok-id van een leeg blok dat de adviseur zelf gaat schrijven
    _toast: { state: true },
    _templateName: { state: true },
  };

  static styles = [css`${unsafeCSS(base)}`, reportUiStyles, css`
    :host { display:block; }
    .error { color:var(--mv-danger); font-size:.82rem; margin:6px 0; }
    .muted { font-size:.8rem; color:var(--mv-text-muted); }
    .card { background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:var(--mv-radius-lg, 14px); box-shadow:var(--mv-shadow-sm); }

    /* Kopbalk */
    .top { padding:14px 18px; display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-bottom:14px; }
    .back { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:8px; width:34px; height:34px; font-size:.95rem;
            color:var(--mv-text-sub); cursor:pointer; flex:none; }
    .back:hover { border-color:var(--mv-primary); color:var(--mv-primary); }
    .ttl { flex:1; min-width:260px; display:flex; flex-direction:column; gap:3px; }
    .ttl div { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .ttl b { font-size:1rem; font-weight:700; letter-spacing:-.02em; color:var(--mv-text); }
    .ttl small { font-size:.78rem; color:var(--mv-text-muted); }
    .open { border:none; border-radius:999px; padding:6px 12px; font:inherit; font-size:.78rem; font-weight:700; cursor:pointer; display:flex; align-items:center;
            gap:6px; white-space:nowrap; background:var(--mv-danger-bg); color:#D9214E; }
    .open::before { content:''; width:6px; height:6px; border-radius:50%; background:currentColor; }
    .open.done { background:var(--mv-primary-tint); color:var(--mv-primary); cursor:default; }
    .toast { display:inline-block; background:var(--mv-primary-tint); color:var(--mv-primary); border-radius:9px; padding:8px 12px; font-size:.8125rem;
             font-weight:600; margin:-4px 0 14px; animation:in .25s ease-out; }
    .wordinfo { font-size:.78rem; color:var(--mv-text-sub); margin:-6px 0 12px; }
    @keyframes in { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }

    .layout { display:grid; grid-template-columns:232px minmax(0,1fr) 300px; gap:16px; align-items:start; }
    @media (max-width:1280px) { .layout { grid-template-columns:210px minmax(0,1fr); } .src { display:none; } }
    @media (max-width:820px) { .layout { grid-template-columns:minmax(0,1fr); } .toc { display:none; } }

    /* Inhoud */
    .toc, .src { position:sticky; top:16px; max-height:calc(100vh - 32px); overflow-y:auto; }
    .toc { padding:12px 8px; }
    .toc .caps { display:block; padding:0 10px 8px; }
    .toc button { width:100%; border:none; background:none; border-radius:7px; padding:6px 10px; display:flex; align-items:baseline; gap:8px;
                  cursor:pointer; text-align:left; font:inherit; }
    .toc button:hover { background:var(--mv-surface-alt); }
    .toc button.on { background:var(--mv-primary-tint); }
    .toc .n { font-size:.75rem; color:var(--mv-text-muted); font-variant-numeric:tabular-nums; flex:none; }
    .toc .t { flex:1; font-size:.8125rem; line-height:1.35; color:var(--mv-text-sub); font-weight:500; }
    .toc .d1 .t { color:var(--mv-text); font-weight:700; }
    .toc button.on .t { color:var(--mv-primary); }
    .toc .dot { width:6px; height:6px; border-radius:50%; flex:none; align-self:center; background:#F1416C; }
    .toc .dot.ai { background:#7C4DDB; }

    /* Document */
    .doc { padding:36px 44px 48px; min-width:0; display:flex; flex-direction:column; gap:8px; }
    @media (max-width:820px) { .doc { padding:22px 18px; } }
    .sec { display:flex; flex-direction:column; gap:12px; scroll-margin-top:16px; }
    .sec.l1 { padding-top:30px; }
    .sec.l2, .sec.l3 { padding-top:12px; }
    .sec.first { padding-top:0; }
    .sec h2, .sec h3, .sec h4 { margin:0; display:flex; gap:12px; color:var(--mv-text); }
    .sec h2 { font-size:1.3125rem; font-weight:700; letter-spacing:-.02em; }
    .sec h3 { font-size:1rem; font-weight:700; letter-spacing:-.01em; }
    .sec h4 { font-size:.875rem; font-weight:700; }
    .sec .hn { color:var(--mv-text-muted); font-weight:600; }

    .block { border-radius:10px; padding:12px 14px; margin:0 -14px; outline:1.5px solid transparent; cursor:pointer; display:flex; flex-direction:column;
             gap:10px; transition:outline-color .15s, background .15s; }
    .block:hover { outline-color:var(--mv-border); }
    .block.sel { outline-color:var(--mv-primary); background:var(--mv-primary-light); }
    .block.stale { outline-color:var(--mv-warning-panel-border); }
    .bhead { display:flex; align-items:center; gap:8px; }
    .bhead .cap { font-size:.75rem; color:var(--mv-text-sub); font-weight:600; }
    .bhead .sp { flex:1; }
    .bhead .edited { font-size:.72rem; color:var(--mv-text-muted); }
    .lock { border:none; background:none; color:var(--mv-text-muted); border-radius:6px; padding:3px 8px; font:inherit; font-size:.72rem; font-weight:600;
            cursor:pointer; display:flex; align-items:center; gap:5px; opacity:.7; }
    .block.sel .lock, .lock:hover { opacity:1; }
    .lock.on { background:var(--mv-primary-tint); color:var(--mv-primary); opacity:1; }
    .stalebar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; background:var(--mv-warning-panel-bg); color:var(--mv-warning-text);
                border-radius:7px; padding:6px 10px; font-size:.75rem; font-weight:600; }
    .stalebar button { border:1px solid var(--mv-warning-panel-border); background:var(--mv-surface); color:var(--mv-text); border-radius:6px;
                       padding:2px 9px; font:inherit; font-size:.72rem; font-weight:600; cursor:pointer; }

    .fields { display:flex; flex-direction:column; }
    .frow { display:grid; grid-template-columns:minmax(120px, 190px) minmax(0,1fr); gap:12px; align-items:center; padding:3px 0; }
    .frow label { font-size:.8125rem; color:var(--mv-text-sub); }
    .frow label .req { color:var(--mv-danger); margin-left:4px; }
    .frow input { border:1px solid var(--mv-border-soft); background:var(--mv-surface-alt); border-radius:7px; padding:6px 9px; font:inherit; font-size:.84rem;
                  color:var(--mv-text); outline:none; min-width:0; }
    .frow input:focus { border-color:var(--mv-primary); background:var(--mv-surface); }
    .frow input.miss { border-color:var(--mv-danger); background:var(--mv-danger-bg); }

    textarea.seamless { border:none; background:transparent; padding:0; font:inherit; font-size:.875rem; line-height:1.65; color:var(--mv-text);
                        resize:none; outline:none; width:100%; overflow:hidden; min-height:1.65em; }
    textarea.seamless::placeholder { color:var(--mv-text-muted); }
    .pending { border:1px dashed #DCCEF7; background:#FAF7FE; border-radius:9px; padding:12px 14px; display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .pending.manual { border-color:var(--mv-border); background:var(--mv-surface-alt); }
    .pending span:first-child { flex:1; min-width:200px; font-size:.8125rem; color:var(--mv-text-sub); line-height:1.5; }
    .pending span:last-child { font-size:.78rem; color:var(--mv-text-muted); }
    :host-context([data-theme='dark']) .pending { background:#221B33; border-color:#4A3B70; }
    :host-context([data-theme='dark']) .pending.manual { background:var(--mv-surface-alt); border-color:var(--mv-border); }

    .notices { display:flex; gap:6px; flex-wrap:wrap; }
    .notice { font-size:.75rem; font-weight:600; border-radius:6px; padding:4px 9px; background:var(--mv-warning-panel-bg); color:var(--mv-warning-text); }
    .notice.error { background:var(--mv-danger-bg); color:var(--mv-danger); }
    .ttitle { font-size:.78rem; font-weight:700; color:var(--mv-text); }
    .twrap { overflow-x:auto; border:1px solid var(--mv-border); border-radius:9px; }
    table.rt { border-collapse:collapse; width:100%; font-size:.78rem; color:var(--mv-text-sub); }
    table.rt td { padding:8px 10px; vertical-align:top; line-height:1.5; border-top:1px solid var(--mv-border-soft); }
    table.rt tr:first-child td { border-top:none; }
    table.rt td + td { border-left:1px solid var(--mv-border-soft); }
    table.rt tr.header td { background:var(--mv-surface-alt); font-size:.72rem; font-weight:700; color:var(--mv-text-sub); line-height:1.35; }
    table.rt tr.group td { background:var(--mv-surface-alt); font-weight:700; color:var(--mv-text); }
    table.rt tr.data td:first-child { color:var(--mv-text); font-weight:600; }
    table.rt .empty { color:var(--mv-text-muted); }

    .vtext p { margin:0 0 6px; font-size:.875rem; color:var(--mv-text); line-height:1.65; }
    .vnone { font-size:.8125rem; color:var(--mv-warning-text); background:var(--mv-warning-panel-bg); border-radius:7px; padding:6px 10px; }
    .why { font-size:.75rem; color:var(--mv-text-sub); }
    .linkbtn { align-self:flex-start; border:none; background:none; padding:0; font:inherit; font-size:.75rem; font-weight:700; color:var(--mv-primary); cursor:pointer; }
    .variants { border:1px solid var(--mv-border); border-radius:9px; padding:8px; background:var(--mv-surface); display:flex; flex-direction:column; gap:4px; cursor:default; }
    .variant { display:flex; gap:8px; align-items:flex-start; padding:6px; border-radius:6px; cursor:pointer; font-size:.8125rem; color:var(--mv-text); }
    .variant:hover { background:var(--mv-surface-alt); }
    .variant.nomatch { opacity:.6; }
    .variant input { accent-color:var(--mv-primary); margin-top:3px; }
    .vreason { font-size:.72rem; color:var(--mv-text-sub); margin-top:1px; }
    .vpreview { font-size:.75rem; color:var(--mv-text-muted); margin-top:3px; white-space:pre-line; }
    .vactions { display:flex; gap:8px; margin-top:4px; flex-wrap:wrap; }

    .apps { display:flex; flex-direction:column; }
    .app { display:grid; grid-template-columns:76px minmax(0,1fr) auto; gap:10px; align-items:center; padding:7px 0; border-top:1px solid var(--mv-border-soft); font-size:.84rem; }
    .app span:first-child { color:var(--mv-text-muted); font-size:.78rem; }
    .app span:nth-child(2) { color:var(--mv-text); }
    .app .ok { font-size:.75rem; font-weight:600; color:var(--mv-primary); }
    .app .miss { font-size:.75rem; font-weight:600; color:var(--mv-danger); }

    /* Waar komt dit vandaan */
    .src { padding:16px 18px; display:flex; flex-direction:column; gap:14px; }
    .src .h { display:flex; flex-direction:column; gap:4px; }
    .src .h b { font-size:.9375rem; font-weight:700; letter-spacing:-.01em; color:var(--mv-text); }
    .src p { margin:0; font-size:.78rem; color:var(--mv-text-sub); line-height:1.55; }
    .items { display:flex; flex-direction:column; }
    .item { display:flex; flex-direction:column; gap:3px; padding:9px 0; border-top:1px solid var(--mv-border-soft); }
    .item div { display:flex; align-items:baseline; gap:8px; }
    .item div b { flex:1; font-size:.8125rem; font-weight:600; color:var(--mv-text); }
    .item .path { font-size:.75rem; color:var(--mv-text-muted); }
    .item .val { font-size:.78rem; color:var(--mv-text); word-break:break-word; }
    .item .val.none { color:var(--mv-text-muted); }
    .item .val.miss { color:var(--mv-danger); font-weight:600; }
    .src .foot { border-top:1px dashed var(--mv-border); padding-top:12px; font-size:.75rem; color:var(--mv-text-sub); line-height:1.5; }
    .src .foot b { color:var(--mv-text); }
    .empty-state { text-align:center; padding:40px 16px; color:var(--mv-text-muted); }
  `];

  constructor() {
    super();
    this._data = null;
    this._loading = true;
    this._filling = false;
    this._error = '';
    this._selected = null;
    this._activeSec = 'cover';
    this._savingBlock = null;
    this._writing = null;
    this._toast = '';
    this._templateName = '';
    this.projectBorings = [];
    this.fillOnOpen = false;
    this._word = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._connect();
  }

  disconnectedCallback() {
    this._closed = true;
    clearTimeout(this._reloadTimer);
    clearTimeout(this._retryTimer);
    clearTimeout(this._toastTimer);
    try { this._ws?.close(); } catch (_) {}
    super.disconnectedCallback();
  }

  updated(changed) {
    if (changed.has('reportId') && this.reportId) this._load({ autoFill: this.fillOnOpen });
    if (changed.has('projectId') && this.projectId && !this._ws) this._connect();
    // Teksten groeien mee met hun inhoud: een document, geen invulvakjes.
    this.renderRoot.querySelectorAll('textarea.seamless').forEach((t) => this._grow(t));
  }

  _grow(t) {
    t.style.height = 'auto';
    t.style.height = `${t.scrollHeight}px`;
  }

  _showToast(msg) {
    this._toast = msg;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { this._toast = ''; }, 2600);
  }

  /**
   * Eigen verbinding met de projecthub. Elk bericht is een reden om opnieuw op te halen: de
   * backend bepaalt welke blokken verouderd zijn, dus we hoeven niet te weten wát er veranderde.
   * Een paar berichten vlak na elkaar (een opname verwerkt tien lagen) worden één ophaalactie.
   */
  _connect() {
    if (!this.projectId || this._closed) return;
    try {
      this._ws = api.connectWebSocket(this.projectId);
    } catch (_) { return; }
    this._ws.onmessage = () => {
      clearTimeout(this._reloadTimer);
      this._reloadTimer = setTimeout(() => this._load({ quiet: true }), 800);
    };
    this._ws.onclose = () => {
      this._ws = null;
      if (!this._closed) this._retryTimer = setTimeout(() => this._connect(), 5000);
    };
  }

  async _load({ quiet = false, autoFill = false } = {}) {
    const id = this.reportId;
    if (!quiet) this._loading = true;
    this._error = '';
    try {
      const d = await api.getReport(id);
      if (id !== this.reportId) return;
      this._data = d;
      if (!this._templateName) this._loadTemplateName(d.report.templateId);
      // Net aangemaakt: meteen vullen, dan opent het rapport ingevuld.
      if (autoFill && !d.blocks.length && d.report.status === 'draft') {
        await this._fill();
        if (!this._error) this._showToast('Rapport aangemaakt en ingevuld uit de projectdata');
      }
    } catch (e) {
      if (!quiet) this._error = e.message || 'Rapport laden mislukt.';
    } finally {
      this._loading = false;
    }
  }

  async _loadTemplateName(templateId) {
    try {
      const list = (await api.getReportTemplates())?.templates || [];
      // Zonder gekozen sjabloon gebruikt het rapport het standaardsjabloon van het bureau.
      const t = templateId ? list.find((x) => x.id === templateId) : list.find((x) => x.isDefault);
      this._templateName = t?.name || 'GeoVoice standaard';
    } catch (_) {
      this._templateName = 'GeoVoice standaard';
    }
  }


  /**
   * Opnieuw vullen zet alles terug naar de projectdata, ook wat de adviseur heeft aangepast;
   * alleen vastgezette blokken blijven staan. Dat gooit werk weg, dus eerst vragen, en zeggen
   * hoeveel aangepaste blokken het raakt.
   */
  async _refill() {
    const blocks = this._data?.blocks || [];
    const edited = blocks.filter((b) => b.state === 'edited').length;
    const locked = blocks.filter((b) => b.state === 'locked').length;
    const n = (k, een, veel) => `${k} ${k === 1 ? een : veel}`;
    const ok = await confirmDialog({
      title: 'Opnieuw vullen?',
      message: [
        'Alles wordt opnieuw uit de projectdata gehaald.',
        edited ? `${n(edited, 'aangepast blok gaat', 'aangepaste blokken gaan')} terug naar de data; je wijzigingen daarin gaan verloren.` : '',
        locked ? `${n(locked, 'vastgezet blok blijft', 'vastgezette blokken blijven')} staan.` : 'Wil je een aanpassing houden, zet het blok dan eerst vast.',
      ].filter(Boolean).join(' '),
      confirmLabel: 'Opnieuw vullen',
      danger: edited > 0,
    });
    if (!ok) return;
    await this._fill();
    if (this._error) return;
    this._showToast(['Opnieuw gevuld uit de projectdata',
      edited ? `${n(edited, 'aangepast blok', 'aangepaste blokken')} teruggezet` : '',
      locked ? `${n(locked, 'vastgezet blok', 'vastgezette blokken')} overgeslagen` : ''].filter(Boolean).join(' · '));
  }

  async _fill() {
    this._filling = true;
    this._error = '';
    try {
      this._data = await api.fillReport(this.reportId);
    } catch (e) {
      this._error = e.message || 'Vullen mislukt.';
    } finally {
      this._filling = false;
    }
  }

  async _updateBlock(block, body) {
    this._savingBlock = block.id;
    this._error = '';
    try {
      await api.updateReportBlock(block.id, body);
      await this._load({ quiet: true });
    } catch (e) {
      this._error = e.message || 'Opslaan mislukt.';
    } finally {
      this._savingBlock = null;
    }
  }

  /**
   * Word: boorprofielen tekenen en rasteren (dezelfde tekenaar als de PDF-export, featureplan
   * §9 optie a), het bestand laten maken, en downloaden. Alleen de boringen van de gekozen
   * deellocaties; zonder keuze alle actieve boringen.
   */
  async _generateWord() {
    const report = this._data?.report;
    if (!report) return;
    const chosen = new Set(report.sublocationIds || []);
    const borings = (this.projectBorings || [])
      .filter((b) => !b.deletedAt && (!chosen.size || (b.sublocationIds || []).some((id) => chosen.has(id))))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'nl', { numeric: true }));
    this._word = { status: 'Boorprofielen tekenen…' };
    try {
      // Zoals de export: twee profielen naast elkaar, in de regel vier per pagina, met één legenda
      // per pagina (boorprofielPaginas). Elke pagina gaat als één afbeelding naar de bijlage.
      const ds = borings.map((b) => buildBoorprofielData(b)).filter(Boolean);
      const paginas = ds.length ? await boorprofielPaginas(ds) : [];
      const profiles = [];
      for (const [i, p] of paginas.entries()) {
        this._word = { status: `Boorprofielen tekenen (pagina ${i + 1}/${paginas.length})…` };
        // De kop boven de pagina in Word: welke boringen, met de schaal erachter (in de PDF staat
        // die in de voet). Een pagina met alleen de legenda heet 'legenda'.
        const namen = p.namen.length > 1 ? `${p.namen.slice(0, -1).join(', ')} en ${p.namen.at(-1)}` : (p.namen[0] || 'legenda');
        const schaal = p.schaal ? ` (${p.schaal.replace(/^Schaal verticaal /, 'schaal verticaal ')})` : '';
        profiles.push({ name: `${namen}${schaal}`, blob: await (await fetch(p.png)).blob() });
      }
      this._word = { status: 'Word-bestand maken…' };
      const result = await api.generateReport(report.id, profiles);
      this._word = { status: 'Downloaden…', result };
      await this._download();
      this._word = { result };
      this._showToast(`Word-bestand gemaakt · v${report.version}`);
      await this._load({ quiet: true });
    } catch (e) {
      this._word = { error: e.message || 'Word-bestand maken mislukt.' };
    }
  }

  async _download() {
    const { blob, filename } = await api.downloadReport(this.reportId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  _scrollTo(key) {
    this.renderRoot.querySelector(`[data-section="${CSS.escape(key)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _select(b) {
    this._selected = b.id;
    this._activeSec = b.sectionKey.startsWith('appendix.') ? 'appendices' : b.sectionKey;
  }

  // ── Afgeleid ─────────────────────────────────────────────────────────────

  /** Wat nog moet: lege verplichte velden, fouten in tabellen, blokken op oude data. */
  _openPoints(d) {
    const out = [];
    for (const b of d.blocks) {
      const c = b.content || {};
      if (c.kind === 'fields') {
        for (const f of c.fields || []) if (f.required && !String(f.value ?? '').trim()) out.push({ block: b, text: `${f.label} ontbreekt` });
      }
      if (c.kind === 'table') for (const n of c.table?.notices || []) if (n.level === 'error') out.push({ block: b, text: n.message });
      if (d.stale?.[b.id]) out.push({ block: b, text: 'Data gewijzigd' });
    }
    return out;
  }

  /** Lege Flip AI-blokken: die krijgen een paars bolletje in de inhoud. */
  _aiPending(b) {
    return b.blockType === 'generatedText' && !String(b.content?.text ?? '').trim();
  }

  _kind(b) {
    // Een Flip AI-blok dat de adviseur zelf heeft geschreven, is van hem.
    if (b.blockType === 'generatedText') return b.state !== 'auto' && String(b.content?.text ?? '').trim() ? BLOCK_KINDS.manual : BLOCK_KINDS.ai;
    if (b.blockType === 'fixedText') return BLOCK_KINDS.fixed;
    if (b.blockType === 'variantText') return BLOCK_KINDS.variant;
    if (b.origin === 'data') return BLOCK_KINDS.data;
    if (b.blockType === 'manualText') return BLOCK_KINDS.manual;
    return BLOCK_KINDS.data;
  }

  // ── Weergave ─────────────────────────────────────────────────────────────

  render() {
    if (this._loading && !this._data) return html`<div class="muted">Laden…</div>`;
    const d = this._data;
    if (!d) return html`<div class="error">${this._error || 'Rapport niet gevonden.'}</div>`;
    const r = d.report;
    const filled = d.blocks.length > 0;
    const lastFill = [...(d.events || [])].reverse().find((e) => e.action === 'filled');
    const opens = filled ? this._openPoints(d) : [];
    const status = REPORT_STATUS[r.status] || REPORT_STATUS.draft;
    const filledLabel = lastFill ? `gevuld ${this._when(lastFill.createdAt)}` : 'nog niet gevuld';
    return html`
      <div class="card top">
        <button class="back" title="Alle rapporten" @click=${() => this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))}>←</button>
        <div class="ttl">
          <div><b>${r.title || 'Rapport'}</b><span class="status-pill ${status.cls}">${status.label}</span></div>
          <small>${[r.reference, `v${r.version}`, `sjabloon ${this._templateName || '…'}`, filledLabel].filter(Boolean).join(' · ')}</small>
        </div>
        ${filled ? html`
          <button class="open ${opens.length ? '' : 'done'}" title=${opens.map((o) => o.text).join('\n')}
                  @click=${() => { const o = opens[0]; if (!o) return; this._select(o.block); this._scrollTo(o.block.sectionKey); }}>
            ${opens.length ? `${opens.length} open ${opens.length === 1 ? 'punt' : 'punten'}` : 'Alles ingevuld'}</button>` : ''}
        <button class="btn ${filled ? '' : 'primary'}" ?disabled=${this._filling || r.status !== 'draft'} @click=${() => (filled ? this._refill() : this._fill())}>
          ${this._filling ? 'Vullen…' : filled ? 'Opnieuw vullen' : 'Vullen uit data'}</button>
        ${filled ? html`
          ${r.fileKey && !this._word?.status ? html`<button class="btn" @click=${() => this._download().catch((e) => { this._word = { error: e.message }; })}
            title="Het laatst gemaakte Word-bestand">Download</button>` : ''}
          <button class="btn primary" ?disabled=${!!this._word?.status} @click=${() => this._generateWord()}
                  title="Boorprofielen tekenen, het Word-bestand maken op het sjabloon van het bureau en downloaden">
            ${this._word?.status || 'Word genereren'}</button>` : ''}
      </div>
      ${this._toast ? html`<div class="toast">${this._toast}</div>` : ''}
      ${this._error ? html`<div class="error">${this._error}</div>` : ''}
      ${this._word?.error ? html`<div class="error">${this._word.error}</div>` : ''}
      ${this._word?.result?.unknownFields?.length ? html`<div class="wordinfo">Niet ingevuld in het sjabloon:
        ${this._word.result.unknownFields.map((f) => `«${f}»`).join(', ')}</div>` : ''}
      ${filled ? html`
        <div class="layout">
          <nav class="card toc">${this._renderToc(d, opens)}</nav>
          <article class="card doc">${this._renderDoc(d)}</article>
          <aside class="card src">${this._renderSource(d)}</aside>
        </div>` : html`
        <div class="card empty-state">
          ${this._filling ? 'Rapport vullen uit de projectdata…' : html`
            <div style="font-weight:700; color:var(--mv-text); margin-bottom:6px;">Nog niet gevuld</div>
            <div>Met <b>Vullen uit data</b> zet GeoVoice het voorblad, de veldwerkgegevens en alle tabellen klaar uit de veldwerkopdracht,
            de boringen, de deellocaties en de labopdrachten.</div>`}
        </div>`}
    `;
  }

  /** 'vandaag 09:04', 'gisteren 16:20', of de datum. */
  _when(iso) {
    const t = new Date(String(iso).replace(' ', 'T') + (/[zZ+]/.test(String(iso).slice(10)) ? '' : 'Z'));
    if (Number.isNaN(t.getTime())) return formatDateTime(iso);
    const hm = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(t).setHours(0, 0, 0, 0)) / 864e5);
    return days === 0 ? `vandaag ${hm}` : days === 1 ? `gisteren ${hm}` : formatDateTime(iso);
  }

  _blocksBySection(d) {
    const m = new Map();
    for (const b of d.blocks) {
      if (!m.has(b.sectionKey)) m.set(b.sectionKey, []);
      m.get(b.sectionKey).push(b);
    }
    return m;
  }

  _renderToc(d, opens) {
    const openSecs = new Set(opens.map((o) => o.block.sectionKey));
    const aiSecs = new Set(d.blocks.filter((b) => this._aiPending(b)).map((b) => b.sectionKey));
    const nodes = flattenOutline(d.outline.chapters);
    const firstBlock = (key) => d.blocks.find((b) => b.sectionKey === key);
    const item = (key, num, title, depth) => {
      const dot = openSecs.has(key) ? 'open' : aiSecs.has(key) ? 'ai' : '';
      return html`
        <button class="d${depth} ${this._activeSec === key ? 'on' : ''}" style=${`padding-left:${depth <= 1 ? 10 : depth === 2 ? 18 : 30}px`}
                @click=${() => { const b = firstBlock(key); if (b) this._select(b); this._activeSec = key; this._scrollTo(key); }}>
          <span class="n" style=${`min-width:${depth >= 3 ? 34 : 22}px`}>${num}</span>
          <span class="t">${title}</span>
          ${dot ? html`<span class="dot ${dot === 'ai' ? 'ai' : ''}"></span>` : ''}
        </button>`;
    };
    return html`
      <span class="caps">Inhoud</span>
      ${item('cover', '', 'Voorblad', 1)}
      ${nodes.map((n) => item(n.key, n.number, n.title, n.depth))}
      ${d.outline.appendices.length ? item('appendices', '', 'Bijlagen', 1) : ''}
    `;
  }

  _renderDoc(d) {
    const bySection = this._blocksBySection(d);
    const blocks = (key) => (bySection.get(key) || []).map((b) => this._renderBlock(b, d));
    const heading = (n) => {
      const num = n.number ? html`<span class="hn">${n.number}</span>` : '';
      return n.depth <= 1 ? html`<h2>${num}${n.title}</h2>` : n.depth === 2 ? html`<h3>${num}${n.title}</h3>` : html`<h4>${num}${n.title}</h4>`;
    };
    const section = (n) => html`
      <div class="sec l${Math.min(n.depth, 3)}" data-section=${n.key}>
        ${heading(n)}
        ${blocks(n.key)}
      </div>
      ${n.children.map(section)}`;
    return html`
      <div class="sec l1 first" data-section="cover"><h2>Voorblad</h2>${blocks('cover')}</div>
      ${d.outline.chapters.map(section)}
      ${d.outline.appendices.length ? this._renderAppendices(d) : ''}
    `;
  }

  /** Alle bijlagen in één lijst, met of ze klaar zijn. */
  _renderAppendices(d) {
    const facts = d.facts || {};
    const sel = this._activeSec === 'appendices';
    const first = d.blocks.find((b) => b.sectionKey.startsWith('appendix.'));
    return html`
      <div class="sec l1" data-section="appendices">
        <h2>Bijlagen</h2>
        <div class="block ${sel ? 'sel' : ''}" @click=${() => { if (first) this._select(first); this._activeSec = 'appendices'; }}>
          <div class="bhead"><span class="kind k-data">Uit de data</span></div>
          <div class="apps">
            ${d.outline.appendices.map((a) => {
              const ready = (APPENDIX_READY[a.key] || (() => true))(facts);
              const title = a.key === 'appendix.boreholeLogs' && facts['borings.count'] ? `${a.title} (${facts['borings.count']})` : a.title;
              return html`<div class="app"><span>${a.number}</span><span>${title}</span>
                <span class=${ready ? 'ok' : 'miss'}>${ready ? 'Klaar' : 'Ontbreekt'}</span></div>`;
            })}
          </div>
        </div>
      </div>`;
  }

  _renderBlock(b, d) {
    if (b.blockType === 'attachment') return nothing;   // staat in de bijlagenlijst
    const kind = this._kind(b);
    const stale = !!d.stale?.[b.id];
    const busy = this._savingBlock === b.id;
    const editable = d.report.status === 'draft' && b.state !== 'locked';
    const locked = b.state === 'locked';
    const caption = b.content?.kind === 'table' ? (b.content.table?.caption || '') : '';
    return html`
      <div class="block ${this._selected === b.id ? 'sel' : ''} ${stale ? 'stale' : ''}" @click=${() => this._select(b)}>
        <div class="bhead">
          <span class="kind ${kind.cls}">${kind.label}</span>
          ${caption ? html`<span class="cap">· ${caption}</span>` : ''}
          <span class="sp"></span>
          ${b.state === 'edited' ? html`<span class="edited">aangepast</span>` : ''}
          <button class="lock ${locked ? 'on' : ''}" ?disabled=${busy || d.report.status !== 'draft'}
                  title=${locked ? 'Opnieuw vullen laat dit blok met rust. Klik om weer vrij te geven.' : 'Vastzetten: opnieuw vullen laat dit blok dan met rust'}
                  @click=${(e) => { e.stopPropagation(); this._updateBlock(b, { state: locked ? 'edited' : 'locked' }); }}>
            <svg width="10" height="11" viewBox="0 0 10 11" fill="none" aria-hidden="true"><rect x="1" y="5" width="8" height="5.5" rx="1.2" fill="currentColor"></rect><path d="M3 5V3.3a2 2 0 0 1 4 0V5" stroke="currentColor" stroke-width="1.3" fill="none"></path></svg>
            ${locked ? 'Vastgezet' : 'Vastzetten'}</button>
        </div>
        ${stale ? html`
          <div class="stalebar">Data gewijzigd sinds dit blok is gemaakt.
            <button ?disabled=${busy} @click=${(e) => { e.stopPropagation(); this._updateBlock(b, { action: 'refresh' }); }}>Bijwerken</button>
            <button ?disabled=${busy} @click=${(e) => { e.stopPropagation(); this._updateBlock(b, { action: 'keep' }); }}>Zo laten</button>
          </div>` : ''}
        ${this._renderContent(b, editable)}
      </div>`;
  }

  _renderContent(b, editable) {
    const content = b.content || {};
    if (content.kind === 'fields') {
      if (!content.fields?.length) return html`<span class="muted">Nog niets in te vullen.</span>`;
      return html`
        <div class="fields">
          ${content.fields.map((f, i) => {
            const miss = f.required && !String(f.value ?? '').trim();
            return html`
              <div class="frow">
                <label for=${`${b.id}-${i}`}>${f.label}${f.required ? html`<span class="req">*</span>` : ''}</label>
                <input id=${`${b.id}-${i}`} class=${miss ? 'miss' : ''} .value=${f.value ?? ''} placeholder=${f.placeholder || ''} ?disabled=${!editable}
                       @change=${(e) => this._updateBlock(b, { content: { ...content, fields: content.fields.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) } })}>
              </div>`;
          })}
        </div>`;
    }
    if (content.kind === 'table') return this._renderTable(content.table);
    if (content.kind === 'variant') return this._renderVariant(b, content, editable);
    if (content.kind === 'text') {
      const text = content.text || '';
      // Leeg en nog niet aan begonnen: zeggen wat hier komt, in plaats van een leeg vak.
      if (!text.trim() && this._writing !== b.id && editable) {
        const ai = b.blockType === 'generatedText';
        return html`
          <div class="pending ${ai ? '' : 'manual'}" @click=${() => { this._writing = b.id; this.updateComplete.then(() => this.renderRoot.querySelector(`textarea[data-block="${b.id}"]`)?.focus()); }}>
            <span>${PENDING[b.blockType] || PENDING.manualText}</span><span>klik om zelf te schrijven</span>
          </div>`;
      }
      return html`<textarea class="seamless" data-block=${b.id} rows="1" .value=${text} ?disabled=${!editable}
        placeholder="Schrijf hier de tekst…"
        @input=${(e) => this._grow(e.target)}
        @blur=${() => { if (this._writing === b.id) this._writing = null; }}
        @change=${(e) => this._updateBlock(b, { content: { kind: 'text', text: e.target.value } })}></textarea>`;
    }
    return nothing;
  }

  /**
   * Een keuzetekst: de tekst van de gekozen varianten, waarom GeoVoice ze koos, en 'Andere
   * variant' met alle varianten: de passende gewoon, de niet-passende grijs met de reden.
   */
  _renderVariant(b, content, editable) {
    const chosen = new Set(content.chosen || []);
    const options = content.options || [];
    const reasons = options.filter((o) => chosen.has(o.key)).map((o) => `${o.label}: ${o.reason}`);
    const open = this._variantOpen === b.id;
    const pick = open ? this._variantPick : chosen;
    const toggle = (key, on) => {
      const next = new Set(this._variantPick);
      if (on) next.add(key); else next.delete(key);
      this._variantPick = next;
    };
    return html`
      ${content.text
        ? html`<div class="vtext">${content.text.split('\n').map((l) => html`<p>${l}</p>`)}</div>`
        : html`<div class="vnone">Geen variant past bij de data. Kies er zelf een met <b>Andere variant</b>.</div>`}
      ${reasons.length ? html`<div class="why">${b.state === 'edited' ? 'Gekozen door de adviseur' : 'Gekozen omdat'}: ${reasons.join(' · ')}</div>` : ''}
      ${editable && options.length ? html`
        <button class="linkbtn" @click=${(e) => { e.stopPropagation(); this._variantOpen = open ? null : b.id; this._variantPick = new Set(chosen); }}>
          ${open ? 'Sluiten' : 'Andere variant'}</button>` : ''}
      ${open ? html`
        <div class="variants" @click=${(e) => e.stopPropagation()}>
          ${options.map((o) => html`
            <label class="variant ${o.match ? '' : 'nomatch'}">
              <input type="checkbox" .checked=${pick.has(o.key)} @change=${(e) => toggle(o.key, e.target.checked)}>
              <span><b>${o.label}</b>${o.match ? '' : html` <span class="tag t-grey">past niet</span>`}
                <div class="vreason">${o.match ? 'Past: ' : 'Past niet: '}${o.reason}</div>
                <div class="vpreview">${o.text}</div></span>
            </label>`)}
          <div class="vactions">
            <button class="btn primary" ?disabled=${this._savingBlock === b.id}
                    @click=${async () => { await this._updateBlock(b, { variantKeys: [...this._variantPick] }); this._variantOpen = null; }}>
              Deze varianten gebruiken</button>
            ${b.state === 'edited' ? html`
              <button class="btn" @click=${async () => { await this._updateBlock(b, { action: 'refresh' }); this._variantOpen = null; }}>
                Terug naar de keuze van GeoVoice</button>` : ''}
          </div>
        </div>` : ''}
    `;
  }

  _renderTable(t) {
    if (!t) return nothing;
    const dataRows = t.rows.filter((r) => r.kind !== 'header');
    return html`
      ${(t.notices || []).length ? html`<div class="notices">${t.notices.map((n) => html`<span class="notice ${n.level}">${n.message}</span>`)}</div>` : ''}
      ${t.title ? html`<div class="ttitle">${t.title}</div>` : ''}
      <div class="twrap">
        <table class="rt">
          ${t.rows.map((row) => html`
            <tr class=${row.kind}>
              ${row.cells.map((cell) => html`
                <td colspan=${cell.colSpan || 1} rowspan=${cell.rowSpan || 1}
                    style=${cell.fill && !FORMAT_FILLS.has(String(cell.fill).toUpperCase()) ? `background:#${cell.fill}` : ''}
                    class=${cell.lines.join('') === '' ? 'empty' : ''}>
                  ${cell.lines.join('') === '' ? '—' : cell.lines.map((l, i) => html`${i ? html`<br>` : ''}${l}`)}</td>`)}
            </tr>`)}
          ${dataRows.length ? '' : html`<tr><td colspan="99" class="empty">Nog geen gegevens voor deze tabel.</td></tr>`}
        </table>
      </div>`;
  }

  // ── Waar komt dit vandaan ────────────────────────────────────────────────

  _sectionTitle(d, key) {
    if (key === 'cover') return 'Voorblad';
    if (key.startsWith('appendix.')) return 'Bijlagen';
    const n = flattenOutline(d.outline.chapters).find((x) => x.key === key);
    return n ? `${n.number} ${n.title}` : key;
  }

  _item(label, tagKey, path, value, { missing = false } = {}) {
    const tag = SOURCE_TAGS[tagKey] || SOURCE_TAGS.report;
    // 'Projectnummer' onder 'Projectnummer', of 'Project › Opdrachtgever' onder 'Opdrachtgever':
    // dat zegt niets nieuws.
    const last = String(path || '').split('›').pop().trim().toLowerCase();
    if (last === String(label).trim().toLowerCase()) path = '';
    return html`
      <div class="item">
        <div><b>${label}</b><span class="tag ${tag.cls}">${tag.label}</span></div>
        ${path ? html`<span class="path">${path}</span>` : ''}
        ${value === undefined ? '' : html`<span class="val ${missing ? 'miss' : value ? '' : 'none'}">${missing ? 'Ontbreekt' : (value || '—')}</span>`}
      </div>`;
  }

  _renderSource(d) {
    const b = d.blocks.find((x) => x.id === this._selected) || d.blocks[0];
    const foot = html`<div class="foot"><b>Vastzetten</b> bewaart jouw versie van een blok. Opnieuw vullen zet al het andere terug naar de projectdata, ook wat je hebt aangepast.</div>`;
    if (!b) return foot;
    const kind = this._kind(b);
    const c = b.content || {};
    let title = this._sectionTitle(d, b.sectionKey);
    let note = '';
    let items = [];
    let action = null;

    if (b.sectionKey.startsWith('appendix.')) {
      const facts = d.facts || {};
      note = 'De bijlagen komen bij het genereren van het Word-bestand uit het project. Wat ontbreekt, vult zich zodra de gegevens er zijn.';
      const src = { 'appendix.samplingPlan': 'sublocations', 'appendix.boreholeLogs': 'borings', 'appendix.labCertificates': 'lab',
        'appendix.assessments': 'lab', 'appendix.preliminaryInvestigation': 'fieldOrder', 'appendix.crow400': 'fieldOrder' };
      items = d.outline.appendices.map((a) => {
        const ready = (APPENDIX_READY[a.key] || (() => true))(facts);
        return this._item(`${a.number} · ${a.title}`, src[a.key] || 'template', '', ready ? 'Klaar' : '', { missing: !ready });
      });
    } else if (c.kind === 'fields') {
      note = 'Velden zijn ingevuld uit het project. Pas je hier iets aan, dan geldt dat alleen voor dit rapport; zet het blok vast om de aanpassing te houden bij opnieuw vullen.';
      // De waarde staat al in het document; hier alleen de herkomst, en of er iets ontbreekt.
      items = (c.fields || []).map((f) => {
        const missing = f.required && !String(f.value ?? '').trim();
        return this._item(f.label, fieldTag(f), f.path || '', missing ? '' : undefined, { missing });
      });
    } else if (c.kind === 'table') {
      const t = c.table;
      const spec = reportTableSpec(t.key);
      title = [t.caption, t.title].filter(Boolean).join(' ');
      const warn = (t.notices || []).map((n) => n.message).join('. ');
      note = warn ? `${warn}. De tabel vult zich zodra de gegevens er zijn.` : 'Opgebouwd uit de projectdata. Gegevens wijzig je in de bron; opnieuw vullen neemt ze over.';
      if (spec) {
        items = [this._item('Rijen', columnTag(spec.main), spec.group), ...spec.cols.map(([name, src, rule]) => this._item(name, columnTag(src), [src, rule].filter(Boolean).join(' — ')))];
      }
    } else if (c.kind === 'variant') {
      note = b.state === 'edited' ? 'Door de adviseur gekozen uit de keuzeteksten van het bureau.' : 'GeoVoice kiest de keuzetekst op basis van de resultaten. Onder het blok kies je zelf een andere.';
      items = (c.options || []).filter((o) => (c.chosen || []).includes(o.key)).map((o) => this._item(o.label, 'template', o.reason));
    } else if (b.blockType === 'fixedText') {
      note = 'Standaardtekst uit de bureauteksten. Je kunt hem hier vrij aanpassen.';
      items = [this._item('Bureautekst', 'template', `Paragraaf ${b.sectionKey}`)];
      if (b.state === 'edited') action = { label: 'Terug naar de bureautekst', run: () => this._updateBlock(b, { action: 'refresh' }) };
    } else if (b.origin === 'data' && c.kind === 'text') {
      note = 'Overgenomen uit de veldwerkopdracht. Wijzig je hem daar, dan werkt opnieuw vullen dit blok bij.';
      const path = b.sectionKey === '2.2.1' ? 'Onderzoek › Aanleiding' : 'Onderzoek › Verdachtheid en verwachte verontreinigingen';
      items = [this._item(b.sectionKey === '2.2.1' ? 'Aanleiding' : 'Vooronderzoek', 'fieldOrder', path)];
    } else if (b.blockType === 'generatedText') {
      note = String(c.text ?? '').trim()
        ? 'Zelf geschreven. Zet het blok vast om je tekst te houden bij opnieuw vullen.'
        : 'Hier komt later een voorstel van Flip AI, op basis van de tabellen en de toetsing. Tot dan schrijf je hem zelf.';
    } else {
      note = 'Door de adviseur te schrijven.';
    }

    return html`
      <div class="h">
        <span class="caps">Waar komt dit vandaan</span>
        <b>${title}</b>
        <span class="kind ${kind.cls}">${kind.label}</span>
      </div>
      ${note ? html`<p>${note}</p>` : ''}
      ${items.length ? html`<div class="items">${items}</div>` : ''}
      ${action ? html`<button class="btn" style="align-self:flex-start" @click=${action.run}>${action.label}</button>` : ''}
      ${foot}
    `;
  }
}

customElements.define('report-editor', ReportEditor);
